import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { finalize, Subscription } from 'rxjs';
import { DashboardService } from '../../../core/services/dashboard.service';
import { AuthService } from '../../../core/services/auth.service';



interface MarketplaceCategory {
  id: string;
  name: string;
  icon: string;
  matches: string[];
}

interface MarketplaceFilter {
  id: string;
  name: string;
  count: number;
}

type MarketplaceSort = 'name' | 'name-desc' | 'rating';

@Component({
  selector: 'app-market',
  standalone: false,
  templateUrl: './marketplace.component.html',
  styleUrls: ['./marketplace.component.css']
})
export class MarketPlaceComponent implements OnInit, OnDestroy {
  dashboardErrorMessage = '';
  isLoading = false;
  selectedCategory = 'all';
  searchText = '';
  currentPage = 1;
  readonly pageSize = 8;
  sortBy: MarketplaceSort = 'name';
  viewMode: 'grid' | 'list' = 'grid';
  applications: MarketplaceApplication[] = [];
  popularApplications: MarketplaceApplication[] = [];
  filteredApplications: MarketplaceApplication[] = [];
  categoryFilters: MarketplaceFilter[] = [];
  tagFilters: MarketplaceFilter[] = [];
  readonly selectedTypes = new Set<string>();
  readonly selectedTags = new Set<string>();
  private readonly failedImages = new Set<string>();
  private marketplaceSubscription?: Subscription;
  private isDestroyed = false;

  // Modelde kategori bulunmadığından sekmeler type metniyle eşleştirilir.
  // API'nizdeki farklı tür adlarını ilgili matches dizisine ekleyebilirsiniz.
  readonly categories: MarketplaceCategory[] = [
    { id: 'all', name: 'Tümü', icon: 'bi-grid', matches: [] },
    { id: 'applications', name: 'Uygulamalar', icon: 'bi-display', matches: ['application', 'applications', 'uygulama', 'uygulamalar', 'website', 'webapp', 'mobile', 'desktop', 'web sitesi', 'mobil', 'masaüstü'] },
    { id: 'saas', name: 'SaaS Hizmetleri', icon: 'bi-cloud', matches: ['saas', 'saas hizmeti', 'saas hizmetleri', 'saas service', 'saas services'] },
    { id: 'games', name: 'Oyunlar', icon: 'bi-controller', matches: ['game', 'games', 'oyun', 'oyunlar'] },
    { id: 'api', name: 'API Hizmetleri', icon: 'bi-code-slash', matches: ['api', 'api hizmeti', 'api hizmetleri', 'api service', 'api services', 'webservice', 'web service', 'microservice'] },
    { id: 'education', name: 'Eğitimler', icon: 'bi-mortarboard', matches: ['education', 'training', 'course', 'eğitim', 'eğitimler'] },
    { id: 'documents', name: 'Dokümanlar', icon: 'bi-file-earmark-text', matches: ['document', 'documents', 'documentation', 'docs', 'ebook', 'e book', 'doküman', 'dokümanlar', 'döküman', 'dökümanlar'] }
  ];

  constructor(
    private readonly router: Router,
    private readonly dashboardSvc: DashboardService,
    private readonly auth: AuthService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.LoadApplications();
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.marketplaceSubscription?.unsubscribe();
  }

  LoadApplications(): void {
    if (this.isLoading) return;
    this.marketplaceSubscription?.unsubscribe();
    this.dashboardErrorMessage = '';
    this.isLoading = true;

    // Mevcut servis metodu ve { data: MarketplaceApplication[] } sözleşmesi korunur.
    this.marketplaceSubscription = this.dashboardSvc.GetMarletplaceApplications()
      .pipe(finalize(() => {
        this.isLoading = false;
        if (!this.isDestroyed) this.changeDetectorRef.detectChanges();
      }))
      .subscribe({
        next: response => {
          const result = response as { data?: MarketplaceApplication[] | null; isError?: boolean } | null;
          if (!result || result.isError || !Array.isArray(result.data)) {
            this.ShowLoadingError('Uygulama listesi alınamadı. Lütfen yeniden deneyin.');
            return;
          }

          this.applications = response.data;
          this.failedImages.clear();
          this.BuildFilters();
          this.popularApplications = [...this.applications]
            .filter(application => application.rating > 0)
            .sort((left, right) => right.rating - left.rating || left.name.localeCompare(right.name, 'tr'))
            .slice(0, 4);
          this.ApplyFilters();
        },
        error: () => this.ShowLoadingError('Uygulamalar yüklenirken bir sorun oluştu. Lütfen yeniden deneyin.')
      });
  }

  get pagedApplications(): MarketplaceApplication[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredApplications.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    return Math.ceil(this.filteredApplications.length / this.pageSize);
  }

  get pageNumbers(): number[] {
    const start = Math.max(1, Math.min(this.currentPage - 2, this.totalPages - 4));
    return Array.from({ length: Math.min(5, this.totalPages) }, (_, index) => start + index);
  }

  get resultStart(): number {
    return this.filteredApplications.length ? (this.currentPage - 1) * this.pageSize + 1 : 0;
  }

  get resultEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.filteredApplications.length);
  }

  get hasActiveFilters(): boolean {
    return Boolean(this.searchText.trim()) || this.selectedCategory !== 'all' || this.selectedTypes.size > 0 || this.selectedTags.size > 0;
  }

  SelectCategory(categoryId: string): void {
    this.selectedCategory = categoryId;
    this.selectedTypes.clear();
    this.ApplyFilters();
  }

  ToggleType(typeId: string): void {
    this.selectedTypes.has(typeId) ? this.selectedTypes.delete(typeId) : this.selectedTypes.add(typeId);
    this.ApplyFilters();
  }

  ToggleTag(tagId: string): void {
    this.selectedTags.has(tagId) ? this.selectedTags.delete(tagId) : this.selectedTags.add(tagId);
    this.ApplyFilters();
  }

  ClearFilters(): void {
    this.searchText = '';
    this.selectedCategory = 'all';
    this.selectedTypes.clear();
    this.selectedTags.clear();
    this.ApplyFilters();
  }

  ApplyFilters(): void {
    const query = this.Normalize(this.searchText);
    const category = this.categories.find(item => item.id === this.selectedCategory);
    const categoryTypes = category?.matches.map(type => this.Normalize(type)) ?? [];

    this.filteredApplications = this.applications.filter(application => {
      const type = this.Normalize(application.type);
      const searchableText = this.Normalize([application.name, application.type, application.description, ...application.tags].join(' '));
      const matchesSearch = !query || searchableText.includes(query);
      const matchesCategory = this.selectedCategory === 'all' || categoryTypes.includes(type);
      const matchesType = !this.selectedTypes.size || this.selectedTypes.has(type);
      const matchesTags = !this.selectedTags.size || application.tags.some(tag => this.selectedTags.has(this.TagKey(tag)));
      return matchesSearch && matchesCategory && matchesType && matchesTags;
    });

    this.filteredApplications.sort((left, right) => {
      if (this.sortBy === 'rating') return right.rating - left.rating || left.name.localeCompare(right.name, 'tr');
      return this.sortBy === 'name-desc'
        ? right.name.localeCompare(left.name, 'tr')
        : left.name.localeCompare(right.name, 'tr');
    });
    this.currentPage = 1;
  }

  ChangePage(page: number): void {
    if (page >= 1 && page <= this.totalPages) this.currentPage = page;
  }

  OpenApplication(application: MarketplaceApplication): void {
    if (application.id) this.router.navigate([application.link]);
  }

  OpenAllApplications(): void {
    this.ClearFilters();
  }

  GoToLogin(): void {
    this.auth.logout();
    this.router.navigateByUrl('/login');
  }

  HasImage(application: MarketplaceApplication): boolean {
    return Boolean(application.imageSrc) && !this.failedImages.has(application.imageSrc);
  }

  OnImageError(application: MarketplaceApplication): void {
    this.failedImages.add(application.imageSrc);
  }

  FormatTag(tag: string): string {
    return '#' + tag.trim().replace(/^#+/, '');
  }

  TrackApplication(index: number, application: MarketplaceApplication): string {
    return application.id || String(index);
  }

  TrackFilter(index: number, filter: MarketplaceFilter): string {
    return filter.id;
  }

  private BuildFilters(): void {
    const types = new Map<string, MarketplaceFilter>();
    const tags = new Map<string, MarketplaceFilter>();
    for (const application of this.applications) {
      const typeId = this.Normalize(application.type);
      if (typeId) {
        const existing = types.get(typeId);
        if (existing) existing.count++;
        else types.set(typeId, { id: typeId, name: application.type, count: 1 });
      }
      for (const tagId of new Set(application.tags.map(tag => this.TagKey(tag)))) {
        const existing = tags.get(tagId);
        if (existing) existing.count++;
        else {
          const original = application.tags.find(tag => this.TagKey(tag) === tagId) ?? tagId;
          tags.set(tagId, { id: tagId, name: original.trim().replace(/^#+/, ''), count: 1 });
        }
      }
    }
    this.categoryFilters = [...types.values()].sort((left, right) => left.name.localeCompare(right.name, 'tr'));
    this.tagFilters = [...tags.values()].sort((left, right) => right.count - left.count || left.name.localeCompare(right.name, 'tr'));
  }

  private Normalize(value: string): string {
    return value.toLocaleLowerCase('tr-TR').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/ı/g, 'i').replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  }

  private TagKey(tag: string): string {
    return this.Normalize(tag.trim().replace(/^#+/, ''));
  }

  private ShowLoadingError(message: string): void {
    this.dashboardErrorMessage = message;
    this.changeDetectorRef.markForCheck();
  }
}
